import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Layout, Tree, Button, Space, Tabs, Table, Typography, Tag, Tooltip,
  message, Drawer, Empty, Spin, Modal,
} from 'antd';
import {
  PlayCircleOutlined, ReloadOutlined, HistoryOutlined,
  DatabaseOutlined, TableOutlined, DownloadOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import Editor, { loader as monacoLoader } from '@monaco-editor/react';
import {
  listVaultSchemas, listVaultTables, getVaultTableSchema,
  queryVault, getVaultQueryHistory,
} from '../services/dataPlatformService';
import { useTheme } from '../contexts/ThemeContext';

const THEME_CARD_BG = 'var(--theme-cardBg, #fff)';
const DEFAULT_TABLE_SCROLL_Y = 320;
const MIN_TABLE_SCROLL_Y = 160;
const RESULTS_CHROME_HEIGHT = 120;
const HISTORY_FETCH_LIMIT = 50;

// Load Monaco from our own origin, not cdn.jsdelivr.net. The console serves an
// enforcing CSP with `script-src 'self'`, so the loader's default CDN fetch is
// blocked and the editor never initialises (BUG-1: the tab hung on "Loading…"
// in production). public/monaco/vs is populated by scripts/copy-monaco.js at
// prebuild, so this path is same-origin and works offline.
monacoLoader.config({ paths: { vs: '/monaco/vs' } });

const { Sider, Content } = Layout;
const { Text, Paragraph } = Typography;

const DEFAULT_QUERY = '-- Write SQL against your workspace vault.\n-- Tip: double-click a table on the left to insert SELECT *.\n\nSELECT 1 AS hello;\n';

// ── Pure helpers extracted from SqlWorkbench ──────────────────────────
// Each collapses a chain of ternaries/&&'s that used to live directly in
// the component body (where every one adds to SqlWorkbench's own
// cyclomatic complexity) into a small, independently testable classifier.
// The actual JSX branching moves into small presentational components
// below, each with its own (small) complexity budget — mirroring how the
// rest of this file already isolates cell rendering (e.g. renderCell).

export function resolveEditorTheme(mode) {
  return mode === 'dark' ? 'vs-dark' : 'vs-light';
}

export function resolveSchemaTreeKind({ treeLoading, treeData }) {
  if (treeLoading) {return 'loading';}
  if (!treeData || treeData.length === 0) {return 'empty';}
  return 'ready';
}

export function resolveResultsPanelKind({ error, result }) {
  if (error) {return 'error';}
  if (!result) {return 'empty';}
  return 'ready';
}

export function shouldShowExportButton(result) {
  return Boolean(result?.rows?.length);
}

/**
 * SqlWorkbench — Snowflake-style SQL editor for the workspace vault.
 *
 * Layout:
 *   ┌──────────────┬──────────────────────────────────────┐
 *   │ Schema tree  │ Monaco SQL editor                     │
 *   │              │ [Run ▶]  [Format]  [History]          │
 *   │ ▸ schema     │ ─────────────────────────────────     │
 *   │   ▸ table    │ Results | Query info                   │
 *   └──────────────┴──────────────────────────────────────┘
 *
 * Safety lives on the backend (`/vault/query`): read-only adapter,
 * single-statement, hard row cap, schema-prefix scoping, statement timeout.
 */
export default function SqlWorkbench({ embedded = false, defaultQuery }) {
  // Monaco doesn't read our --theme-* CSS variables, so its editor theme
  // must be switched explicitly when the app is in dark mode.
  const { themeConfig } = useTheme();
  const monacoTheme = resolveEditorTheme(themeConfig?.mode);
  const [treeData, setTreeData] = useState([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loadedKeys, setLoadedKeys] = useState([]);

  // Initial query: caller-provided (e.g. Data Center pre-fills
  // `SELECT * FROM <selected_table>` when the user opens the SQL
  // Workbench tab with a table chosen) or the static placeholder.
  // Initial-only: later sidebar selections do NOT overwrite the editor —
  // the user copies new table/column names via the sidebar Copy icons
  // when they want to compose against a different table.
  //
  // The Monaco editor is uncontrolled (`defaultValue`) so React state
  // round-trips can't fight typing: every keystroke fired `onChange →
  // setSql → re-render → value={sql}`, and on fast input the stale
  // `value` arriving a tick late would force-replace the editor model
  // and snap the cursor to the end. `sql` still mirrors the editor for
  // anything that reads it outside the run path; programmatic content
  // changes (Clear / table double-click / History Load / insert-select)
  // go through `setEditorValue` which writes both state and editor.
  const [sql, setSql] = useState(defaultQuery || DEFAULT_QUERY);
  const editorRef = useRef(null);

  const setEditorValue = useCallback((text) => {
    setSql(text);
    const editor = editorRef.current;
    if (editor && editor.getValue() !== text) {
      editor.setValue(text);
    }
  }, []);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('results');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Results table scroll height — measured from the actual results pane
  // rather than a fixed `100vh` calc. The workbench can be mounted both
  // standalone (`/data/sql`) and embedded inside DATA CENTER (offset by a
  // page header + tab bar), so a viewport-relative height overflows the
  // screen when embedded. We observe the wrapper and subtract the table
  // header + pagination chrome (~120px) so the body fills the remaining space.
  const resultsPaneRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(DEFAULT_TABLE_SCROLL_Y);

  // ── Schema tree ─────────────────────────────────────────────
  const refreshSchemas = useCallback(async () => {
    setTreeLoading(true);
    try {
      const schemas = await listVaultSchemas();
      setTreeData(schemas.map((s) => ({
        title: (
          <Space size={4}>
            <DatabaseOutlined />
            <Text strong>{s.connector}</Text>
            <Tag color="blue" style={{ marginLeft: 4 }}>{s.table_count}</Tag>
          </Space>
        ),
        rawTitle: s.connector,
        key: `schema:${s.schema_name}`,
        schema: s.schema_name,
        isLeaf: false,
      })));
      setLoadedKeys([]);
    } catch (err) {
      message.error(`Failed to load schemas: ${err.message}`);
    } finally {
      setTreeLoading(false);
    }
  }, []);
  // In embedded mode the internal schema tree isn't rendered (the Data
  // Center's shared VaultSidebar drives selection) — skip the initial
  // fetch so we don't duplicate VaultDataContext.loadSchemas().
  useEffect(() => {
    if (embedded) {return;}
    refreshSchemas();
  }, [refreshSchemas, embedded]);

  const onLoadData = useCallback(async (node) => {
    if (node.children && node.children.length > 0) {return;}
    if (!node.schema) {return;}
    try {
      const tables = await listVaultTables(node.schema);
      const children = tables.map((t) => ({
        title: (
          <Space size={4}>
            <TableOutlined />
            <Text>{t.table}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t.row_count.toLocaleString()}
            </Text>
          </Space>
        ),
        rawTitle: t.table,
        key: `table:${node.schema}.${t.table}`,
        schema: node.schema,
        table: t.table,
        isLeaf: true,
      }));
      setTreeData((prev) => addChildren(prev, node.key, children));
      setLoadedKeys((prev) => [...prev, node.key]);
    } catch (err) {
      message.error(`Failed to load tables: ${err.message}`);
    }
  }, []);

  // ── Editor actions ──────────────────────────────────────────
  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    // Ctrl/Cmd+Enter → Run
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
      () => runQuery(),
    );
  };

  const onTreeDoubleClick = (_, { node }) => {
    if (node.table && node.schema) {
      const snippet = `SELECT *\nFROM "${node.schema}"."${node.table}"\nLIMIT 100;`;
      setEditorValue(snippet);
    }
  };

  const onTreeSelect = async (_, { node }) => {
    if (!node.table || !node.schema) {return;}
    try {
      const sch = await getVaultTableSchema(node.schema, node.table);
      Modal.info({
        title: `${node.schema}.${node.table}`,
        width: 520,
        content: (
          <Table
            size="small"
            pagination={false}
            scroll={{ y: 360 }}
            rowKey="name"
            dataSource={sch.columns}
            columns={[
              { title: 'Column', dataIndex: 'name' },
              { title: 'Type', dataIndex: 'type', render: (t) => <Tag>{t}</Tag> },
            ]}
          />
        ),
      });
    } catch (err) {
      message.error(`Failed to load schema: ${err.message}`);
    }
  };

  // ── Run ─────────────────────────────────────────────────────
  const runQuery = useCallback(async () => {
    const text = (editorRef.current?.getValue() ?? sql).trim();
    if (!text) {return;}
    setRunning(true);
    setError(null);
    setResult(null);
    setActiveTab('results');
    try {
      const r = await queryVault(text, 1000);
      setResult(r);
      if (r.truncated) {
        message.warning(`Result truncated to ${r.row_count} rows.`);
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setRunning(false);
    }
  }, [sql]);

  // ── History ─────────────────────────────────────────────────
  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const h = await getVaultQueryHistory(HISTORY_FETCH_LIMIT, false);
      setHistoryItems(h.items || []);
    } catch (err) {
      message.error(`Failed to load history: ${err.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Results table ───────────────────────────────────────────
  const resultColumns = useMemo(() => {
    if (!result?.columns) {return [];}
    return result.columns.map((c, idx) => ({
      title: (
        <Space size={4}>
          <Text strong>{c.name}</Text>
          <Tag style={{ marginLeft: 4 }}>{c.type}</Tag>
        </Space>
      ),
      dataIndex: idx,
      key: c.name,
      ellipsis: true,
      render: (v) => renderCell(v),
    }));
  }, [result]);

  const resultRows = useMemo(() => {
    if (!result?.rows) {return [];}
    return result.rows.map((row, i) => {
      const obj = { key: i };
      row.forEach((v, idx) => { obj[idx] = v; });
      return obj;
    });
  }, [result]);

  const exportCsv = () => {
    if (!result?.rows?.length) {return;}
    const header = result.columns.map((c) => csvCell(c.name)).join(',');
    const lines = result.rows.map((r) => r.map(csvCell).join(','));
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vault_query_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // When embedded inside DATA CENTER, the shared <VaultSidebar> drives
  // the tree. It dispatches a `vault:insert-select` event on double-click;
  // we listen here and inject `SELECT * FROM <schema>.<table> LIMIT 100`.
  useEffect(() => {
    if (!embedded) {return undefined;}
    const onInsert = (e) => {
      const { schema, table } = e.detail || {};
      if (!schema || !table) {return;}
      setEditorValue(`SELECT *\nFROM "${schema}"."${table}"\nLIMIT 100;`);
    };
    window.addEventListener('vault:insert-select', onInsert);
    return () => window.removeEventListener('vault:insert-select', onInsert);
  }, [embedded, setEditorValue]);

  // Keep the results table body sized to its actual container so it never
  // spills past the bottom of the screen (esp. when embedded in DATA CENTER).
  useEffect(() => {
    const el = resultsPaneRef.current;
    if (!el) {return undefined;}
    const update = () => {
      const node = resultsPaneRef.current;
      if (!node) {return;}
      // Distance from the top of the results pane to the bottom of the
      // viewport — this is the true available height regardless of how far
      // down the page the workbench is mounted (embedded vs standalone).
      const top = node.getBoundingClientRect().top;
      // Subtract the table header (~40px), pagination row (~56px) and a small
      // bottom margin (~24px) so the body fills the rest of the screen.
      const next = Math.max(MIN_TABLE_SCROLL_Y, Math.floor(window.innerHeight - top - RESULTS_CHROME_HEIGHT));
      setTableScrollY(next);
    };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) {ro.disconnect();}
      window.removeEventListener('resize', update);
    };
  }, [result, activeTab]);

  return (
    <Layout style={{ height: '100%', background: THEME_CARD_BG }}>
      <WorkbenchSider
        embedded={embedded}
        treeData={treeData}
        treeLoading={treeLoading}
        loadedKeys={loadedKeys}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        onSelect={onTreeSelect}
        onDoubleClick={onTreeDoubleClick}
        onLoadData={onLoadData}
        onRefresh={refreshSchemas}
      />

      <Layout style={{ background: THEME_CARD_BG }}>
        <Content style={{ display: 'flex', flexDirection: 'column', background: THEME_CARD_BG }}>
          <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Space>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={running}
                onClick={runQuery}
              >
                Run (Ctrl+Enter)
              </Button>
              <Button icon={<HistoryOutlined />} onClick={openHistory}>History</Button>
              <Button icon={<ClearOutlined />} onClick={() => setEditorValue('')}>Clear</Button>
            </Space>
          </div>

          <div style={{ flex: '0 0 40%', minHeight: 220, borderBottom: '1px solid var(--theme-border, #f0f0f0)' }}>
            <Editor
              height="100%"
              defaultLanguage="sql"
              theme={monacoTheme}
              defaultValue={sql}
              onChange={(v) => setSql(v ?? '')}
              onMount={handleEditorMount}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                tabSize: 2,
              }}
            />
          </div>

          <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              style={{ height: '100%' }}
              tabBarStyle={{ paddingLeft: 16, marginBottom: 0 }}
              tabBarExtraContent={<ExportCsvButton result={result} onExport={exportCsv} />}
              items={[
                {
                  key: 'results',
                  label: 'Results',
                  children: (
                    <div ref={resultsPaneRef} style={{ padding: 12, height: 'calc(100% - 8px)', overflow: 'hidden' }}>
                      <ResultsPanelBody
                        error={error}
                        result={result}
                        resultRows={resultRows}
                        resultColumns={resultColumns}
                        tableScrollY={tableScrollY}
                      />
                    </div>
                  ),
                },
                {
                  key: 'info',
                  label: 'Query Info',
                  children: (
                    <div style={{ padding: 16 }}>
                      <QueryInfoPanel result={result} />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Content>
      </Layout>

      <Drawer
        title="Query History"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        size={640}
      >
        <QueryHistoryBody
          historyLoading={historyLoading}
          historyItems={historyItems}
          onLoadItem={(r) => { setEditorValue(r.sql); setHistoryOpen(false); }}
        />
      </Drawer>
    </Layout>
  );
}

// ── small presentational panes ──────────────────────────────────────
// Each isolates one section's own branching (loading/empty/ready etc.) in
// its own small function scope, rather than that branching living
// directly in SqlWorkbench's body.

function WorkbenchSider({
  embedded, treeData, treeLoading, loadedKeys, expandedKeys,
  onExpand, onSelect, onDoubleClick, onLoadData, onRefresh,
}) {
  if (embedded) {return null;}
  const kind = resolveSchemaTreeKind({ treeLoading, treeData });
  return (
    <Sider width={360} theme="light" style={{ borderRight: '1px solid var(--theme-border, #f0f0f0)', overflow: 'auto', background: THEME_CARD_BG }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>Schemas</Text>
          <Tooltip title="Refresh">
            <Button size="small" type="text" icon={<ReloadOutlined />} onClick={onRefresh} />
          </Tooltip>
        </Space>
      </div>
      {kind === 'loading' && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}
      {kind === 'empty' && <Empty description="No data ingested yet" style={{ padding: 24 }} />}
      {kind === 'ready' && (
        <Tree
          treeData={treeData}
          loadData={onLoadData}
          loadedKeys={loadedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          onSelect={onSelect}
          onDoubleClick={onDoubleClick}
          blockNode
          style={{ padding: 8 }}
        />
      )}
    </Sider>
  );
}

function ExportCsvButton({ result, onExport }) {
  if (!shouldShowExportButton(result)) {return null;}
  return (
    <Button size="small" icon={<DownloadOutlined />} onClick={onExport} style={{ marginRight: 12 }}>
      CSV
    </Button>
  );
}

function ResultsPanelBody({ error, result, resultRows, resultColumns, tableScrollY }) {
  const kind = resolveResultsPanelKind({ error, result });
  if (kind === 'error') {
    return <Paragraph type="danger" style={{ whiteSpace: 'pre-wrap' }}>{error}</Paragraph>;
  }
  if (kind === 'empty') {
    return <Empty description="Run a query to see results" />;
  }
  return (
    <Table
      size="small"
      dataSource={resultRows}
      columns={resultColumns}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      scroll={{ x: 'max-content', y: tableScrollY }}
      bordered
    />
  );
}

function QueryInfoPanel({ result }) {
  if (!result) {return <Empty description="No query run yet" />;}
  return (
    <Space orientation="vertical">
      <Text>Rows returned: <b>{result.row_count}</b></Text>
      <Text>Elapsed: <b>{result.elapsed_ms} ms</b></Text>
      <Text>Truncated: <b>{result.truncated ? 'yes' : 'no'}</b></Text>
      <Text>Columns: <b>{result.columns.length}</b></Text>
    </Space>
  );
}

function QueryHistoryBody({ historyLoading, historyItems, onLoadItem }) {
  if (historyLoading) {return <Spin />;}
  return (
    <Table
      size="small"
      rowKey="id"
      dataSource={historyItems}
      pagination={{ pageSize: 20 }}
      columns={[
        {
          title: 'When', dataIndex: 'created_at', width: 160,
          render: (v) => new Date(v).toLocaleString(),
        },
        {
          title: 'SQL', dataIndex: 'sql', ellipsis: true,
          render: (v) => (
            <Tooltip title={<pre style={{ margin: 0 }}>{v}</pre>}>
              <code style={{ fontSize: 11 }}>{v.slice(0, 80)}</code>
            </Tooltip>
          ),
        },
        {
          title: 'Rows', dataIndex: 'row_count', width: 70,
          render: (n, r) => r.error ? <Tag color="red">err</Tag> : n,
        },
        { title: 'ms', dataIndex: 'elapsed_ms', width: 70 },
        {
          title: '', width: 80,
          render: (_, r) => (
            <Button size="small" onClick={() => onLoadItem(r)}>Load</Button>
          ),
        },
      ]}
    />
  );
}

// ── helpers ────────────────────────────────────────────────────────
function addChildren(nodes, key, children) {
  return nodes.map((n) => {
    if (n.key === key) {return { ...n, children };}
    if (n.children) {return { ...n, children: addChildren(n.children, key, children) };}
    return n;
  });
}

function renderCell(v) {
  if (v === null || v === undefined) {
    return <Text type="secondary" italic>NULL</Text>;
  }
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return <code style={{ fontSize: 11 }}>{s.length > 120 ? `${s.slice(0, 120)}…` : s}</code>;
  }
  return String(v);
}

function csvCell(v) {
  if (v === null || v === undefined) {return '';}
  let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  // CSV formula-injection guard: a cell starting with = + - @ (or a control
  // char) is executed as a formula by Excel/Sheets on open. Warehouse data
  // (member/project names, free text) can contain these, so neutralise with a
  // leading apostrophe before the normal quote/escape.
  if (/^[=+\-@\t\r]/.test(s)) {s = `'${s}`;}
  if (/[",\n]/.test(s)) {return `"${s.replace(/"/g, '""')}"`;}
  return s;
}
